/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import Header from '../Header';
import {
  DashboardDataFilter,
  DeviceAttributes,
  ParametersType,
  ParticleData,
  ScrollContainerAttribute
} from '../../constants/telosair-interfaces';
import Loader from '../loader';
import { useData } from '../../context/data.context';
import { getDisplayableValue } from '../../_helpers/globalHelper';
import { getPriorityColor, getclasses } from '../../_helpers/dashboardHelper';
import TanstackTable from '../TanstackTable';
import { Filters } from '../../services/localstorage.service';
import TruncatedText from '../truncatedText';
import ScrollView from '../ScrollContainer';

// const NUMBER_OF_DEVICES_TO_DISPLAY = 8;
const DEFAULT_COLUMN_WIDTH = 40;
type TableAttribute = ParametersType & { serialNumber: number; deviceName: string };

type DeviceDetails = {
  // eslint-disable-next-line react/no-unused-prop-types
  loading: boolean;
  extendedLabel: string;
  dataFilter: DashboardDataFilter | null;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
};

/**
 * Displays a list of devices and their parameter values.
 * @param props - DeviceDetails.
 * @returns A React element representing the Devices component.
 */
export default function DeviceListWidget(props: DeviceDetails): ReactElement {
  const { dataFilter, loading, extendedLabel, setDataFilter } = props;
  const navigate = useNavigate();
  const {
    groupedData,
    parameters,
    devices: data,
    deviceDataSummary,
    clientThresholdSettings,
    userConfigDataV2
  } = useData();
  const [scrollContainerData, setScrollContainerData] = useState<ScrollContainerAttribute>({
    loaded: false,
    height: null,
    width: null
  });
  const [devices, setDevices] = useState<DeviceAttributes[]>([]);
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const [showScrollBtn, setShowScrollBtn] = useState({ left: false, right: true });

  const scrollContainerInnerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (dataFilter?.type === 'group') {
      const filteredGroup: any = groupedData.filter((item) => item.groupId === dataFilter.id)[0];
      if (filteredGroup) {
        const devicesInGroup = filteredGroup.sensorDetailsList?.map(
          (item: any) => item.serial_number
        );
        setDevices(data.filter((item) => devicesInGroup.includes(item.serialNumber)));
      }
    } else {
      setDevices(data);
    }
  }, [data, dataFilter, groupedData]);

  useEffect(() => {
    if (!loading) {
      const tempData: TableAttribute[] = [];
      devices.forEach((device) => {
        const tempParameterData: any = {
          deviceName: device.deviceName,
          serialNumber: device.serialNumber
        } as TableAttribute;

        parameters.forEach((parameter) => {
          const deviceData = deviceDataSummary[device.serialNumber];
          if (
            deviceData &&
            deviceData.parameters &&
            deviceData.parameters[parameter.parameterName]
          ) {
            tempParameterData[parameter.parameterName] =
              deviceData.parameters[parameter.parameterName];
          } else {
            tempParameterData[parameter.parameterName] = -9999;
          }
        });
        tempData.push(tempParameterData);
      });
      setFormattedData(tempData);
    }
  }, [loading, deviceDataSummary, parameters, devices]);

  const selectedDevice = dataFilter?.type === 'device' ? dataFilter.id : null;
  /**
   * Handles device selection.
   * @param deviceSerialNumber - Device serial number.
   */
  const onSelectDevice = (deviceSerialNumber: number): void => {
    if (selectedDevice === deviceSerialNumber) {
      setDataFilter?.(null);
      return;
    }
    const device = devices.find((item) => item.serialNumber === deviceSerialNumber);
    if (device) {
      setDataFilter?.({ id: deviceSerialNumber, type: 'device', name: device?.deviceName || '' });
    }
  };

  const UserTableColumns = useMemo(
    () => [
      {
        id: 'deviceName',
        accessorKey: 'deviceName',
        size: 80,
        header: 'Device Name',
        cell: (info: any) => {
          const rowData = info.row.original;
          const size = info.column?.getSize();
          return (
            <button
              onClick={() => onSelectDevice(rowData.serialNumber)}
              type="button"
              className={`cursor-pointer hover:text-white ${
                selectedDevice === rowData.serialNumber && 'text-white'
              }`}
            >
              <div style={{ maxWidth: size }}>
                <TruncatedText text={info.getValue()} />
              </div>
            </button>
          );
        }
      },
      ...parameters.map((parameter) => ({
        accessorKey: parameter.parameterName,
        header: parameter.shortName,
        id: parameter.parameterName,
        size: DEFAULT_COLUMN_WIDTH,
        cell: (info: any) => {
          // Readings arrive from the API already expressed in the user's
          // selected unit (the backend applies the unit config).
          const converted = Number(info.getValue());
          return (
            <span
              className={getclasses(
                true,
                getPriorityColor(
                  parameter.parameterName,
                  converted,
                  clientThresholdSettings,
                  userConfigDataV2,
                  info.row.original.deviceType
                )
              )}
            >
              {getDisplayableValue(parameter.parameterName, converted)}
            </span>
          );
        }
      }))
    ],
    [parameters, selectedDevice, devices]
  );

  const scrollToLeft = (): void => {
    if (scrollContainerInnerRef.current) {
      const { scrollLeft } = scrollContainerInnerRef.current;
      const prevScroll = scrollLeft - DEFAULT_COLUMN_WIDTH;
      scrollContainerInnerRef.current.scrollTo({
        left: prevScroll,
        behavior: 'smooth' // For smooth scrolling
      });
    }
  };

  const scrollToRight = (): void => {
    if (scrollContainerInnerRef.current) {
      const { scrollLeft } = scrollContainerInnerRef.current;
      const nextScroll = scrollLeft + DEFAULT_COLUMN_WIDTH;
      scrollContainerInnerRef.current.scrollTo({
        left: nextScroll,
        behavior: 'smooth' // For smooth scrolling
      });
    }
  };

  useEffect(() => {
    const handleScroll = (): void => {
      if (scrollContainerInnerRef.current) {
        const { scrollLeft } = scrollContainerInnerRef.current;
        let scroll = { left: true, right: true };
        if (
          scrollContainerInnerRef.current.scrollLeft +
            scrollContainerInnerRef.current.clientWidth >=
          scrollContainerInnerRef.current.scrollWidth
        ) {
          scroll = { ...scroll, right: false };
        }
        if (scrollLeft === 0) {
          scroll = { ...scroll, left: false };
        }
        setShowScrollBtn(scroll);
      }
    };

    const div = scrollContainerInnerRef.current;
    if (div) {
      div.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (div) {
        div.removeEventListener('scroll', handleScroll);
      }
    };
  }, [scrollContainerData.width]);
  return (
    <div className="rounded p-4 flex flex-1 flex-col gap-4 bg-container-500 w-full relative max-h-full h-full  flex-shrink">
      <Header
        actionButton={() => navigate('/settings/device-setup?tab=devices')}
        actionButtonText="Settings"
        sectionName="Devices"
        showIcon
      >
        <span className="text-sm font-regular text-gray-400  cursor-default">{extendedLabel}</span>
      </Header>

      <ScrollView
        scrollContainerData={scrollContainerData}
        loading={loading}
        setScrollContainerData={setScrollContainerData}
      >
        {!loading && (
          <button
            onClick={scrollToLeft}
            style={{
              background: 'linear-gradient(to left, rgba(0, 0, 0, 0), #0e0e16, #0e0e16)',
              visibility: showScrollBtn.left ? 'visible' : 'hidden'
            }}
            className="w-12 flex justify-start absolute top-[-3px] left-0 z-[5]"
            type="button"
          >
            <span className="material-icons-outlined text-xl text-gray-200">chevron_left</span>
          </button>
        )}
        {!loading && (
          <button
            onClick={scrollToRight}
            className="w-8 flex justify-end absolute top-[-3px] right-0 z-[5]"
            style={{
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0), #0e0e16, #0e0e16)',
              visibility: showScrollBtn.right ? 'visible' : 'hidden'
            }}
            type="button"
          >
            <span className="material-icons-outlined text-xl text-gray-200">chevron_right</span>
          </button>
        )}
        {!scrollContainerData.loaded && <Loader />}
        {scrollContainerData.loaded && (
          <div
            ref={scrollContainerInnerRef}
            style={{
              height: scrollContainerData.height || 'auto'
            }}
            className="text-gray-400 text-xs font-normal relative overflow-y-auto overflow-x-auto"
          >
            <TanstackTable
              columOrderStorageKey={Filters.deviceListWidgetColumnOrder}
              tdClassName="py-2 bg-container-500"
              thClassName="justify-center px-1 py-1 bg-container-500"
              data={formattedData}
              columns={UserTableColumns}
            />
          </div>
        )}
      </ScrollView>
    </div>
  );
}
